import React, { useState, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import ContactForm from "./ContactForm";
import ContactBunchForm from "./ContactBunchForm";
import ContactImport from "./ContactImport";
import API_BASE_URL from "../../config/api";
import {
  Typography,
  Row,
  Col,
  Card,
  Button,
  Spin,
  Empty,
  Modal,
  Space,
  Tag,
  Input,
  Select,
  message,
  Tabs,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ImportOutlined,
  UsergroupAddOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import CustomPagination from "../paginations/CustomPagination";
import ContactFormModal from "./ContactForm";
import ContactBunchFormModal from "./ContactBunchForm";
import ContactImportModal from "./ContactImport";

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const ContactList = () => {
  const [contacts, setContacts] = useState([]);
  const [contactBunches, setContactBunches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showBunchForm, setShowBunchForm] = useState(false);
  const [showImportForm, setShowImportForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editingBunch, setEditingBunch] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedBunch, setSelectedBunch] = useState("all");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [bunchPagination, setBunchPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isDeleteContactModalVisible, setIsDeleteContactModalVisible] =
    useState(false);
  const [isDeleteBunchModalVisible, setIsDeleteBunchModalVisible] =
    useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [bunchToDelete, setBunchToDelete] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState("contacts");

  useEffect(() => {
    fetchContacts();
    fetchContactBunches();
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/contacts`, {
        headers: { Authorization: token },
        params: {
          search: searchText,
          bunch_id: selectedBunch === "all" ? undefined : selectedBunch,
        },
      });

      const { contacts: contactsData } = response.data;

      // Ensure contacts is always an array
      const contactsArray = Array.isArray(contactsData) ? contactsData : [];

      const contactsWithBunchName = contactsArray.map((contact) => ({
        ...contact,
        contact_bunch_name: contact.contact_bunch_name || "N/A",
        fullName: `${contact.first_name || ""} ${
          contact.last_name || ""
        }`.trim(),
      }));

      setContacts(contactsWithBunchName);
      setPagination((prev) => ({
        ...prev,
        total: contactsWithBunchName.length,
      }));
    } catch (err) {
      setError("Failed to fetch contacts");
      message.error("Failed to fetch contacts");
    } finally {
      setLoading(false);
    }
  };

  const fetchContactBunches = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/contact-bunches`, {
        headers: { Authorization: token },
      });

      // Fallback for non-paginated response
      const bunchesData = Array.isArray(response.data) ? response.data : [];
      setContactBunches(bunchesData);
      setBunchPagination((prev) => ({
        ...prev,
        total: bunchesData.length,
      }));
    } catch (err) {
      message.error("Failed to fetch contact bunches");
      setContactBunches([]);
    }
  };

  const handleAddContact = () => {
    setEditingContact(null);
    setShowForm(true);
  };

  const handleAddBunch = () => {
    setEditingBunch(null);
    setShowBunchForm(true);
  };

  const handleImportContacts = () => {
    setShowImportForm(true);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setShowForm(true);
  };

  const handleEditBunch = (bunch) => {
    setEditingBunch(bunch);
    setShowBunchForm(true);
  };

  const showDeleteContactModal = (contact) => {
    setContactToDelete(contact);
    setIsDeleteContactModalVisible(true);
  };

  const showDeleteBunchModal = (bunch) => {
    setBunchToDelete(bunch);
    setIsDeleteBunchModalVisible(true);
  };

  const handleDeleteContact = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/contacts/${contactToDelete.id}`, {
        headers: { Authorization: token },
      });
      message.success("Contact deleted successfully");
      fetchContacts();
      setIsDeleteContactModalVisible(false);
      setContactToDelete(null);
    } catch (err) {
      message.error("Failed to delete contact");
    }
  };

  const handleDeleteBunch = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API_BASE_URL}/contact-bunches/${bunchToDelete.id}`,
        {
          headers: { Authorization: token },
        }
      );
      message.success("Contact bunch deleted successfully");
      fetchContactBunches();
      fetchContacts();
      setActiveTabKey("bunches");
      setIsDeleteBunchModalVisible(false);
      setBunchToDelete(null);
    } catch (err) {
      message.error("Failed to delete contact bunch");
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setShowBunchForm(false);
    setShowImportForm(false);
    setEditingContact(null);
    setEditingBunch(null);
  };

  const handleFormSubmit = () => {
    handleFormClose();
    fetchContacts();
    fetchContactBunches();
  };

  // Handle page change for contacts
  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, current: page }));
  };

  // Handle page size change for contacts
  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
      pageSize: newPageSize,
    }));
  };

  // Handle page change for bunches
  const handleBunchPageChange = (page) => {
    setBunchPagination((prev) => ({ ...prev, current: page }));
  };

  // Handle page size change for bunches
  const handleBunchPageSizeChange = (newPageSize) => {
    setBunchPagination((prev) => ({
      ...prev,
      current: 1,
      pageSize: newPageSize,
    }));
  };

  const contactColumns = [
    {
      title: "Name",
      dataIndex: "fullName",
      key: "fullName",
      width: 180,
      sorter: (a, b) => a.fullName.localeCompare(b.fullName),
      render: (text, record) => (
        <Space>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: "#1890ff",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              fontWeight: "bold",
            }}
          >
            {record.first_name?.charAt(0)?.toUpperCase() || "?"}
          </div>
          <div>
            <div style={{ fontWeight: 600 }}>{text}</div>
          </div>
        </Space>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => phone || <Text type="secondary">-</Text>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => email || <Text type="secondary">-</Text>,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (company) => company || <Text type="secondary">N/A</Text>,
    },
    {
      title: "Language",
      dataIndex: "language",
      key: "language",
      render: (language) => language || <Text type="secondary">N/A</Text>,
    },
    {
      title: "Bunch",
      dataIndex: "contact_bunch_name",
      key: "contact_bunch_name",
      render: (contact_bunch_name) =>
        contact_bunch_name || <Text type="secondary">N/A</Text>,
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      render: (notes) => notes || <Text type="secondary">N/A</Text>,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 120,
      render: (date) => dayjs(date).format("DD-MM-YYYY"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit Contact">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "green" }} />}
              onClick={() => handleEditContact(record)}
            />
          </Tooltip>
          <Tooltip title="Delete Contact">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => showDeleteContactModal(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const bunchColumns = [
    {
      title: "Bunch Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name) => <div style={{ fontWeight: 600 }}>{name}</div>,
    },
    {
      title: "Bunch Description",
      dataIndex: "description",
      key: "description",
      render: (description) =>
        description || <Text type="secondary">No description</Text>,
    },
    {
      title: "No of Contacts",
      key: "contacts",
      render: (_, record) => (
        <Tag color="blue">
          {contacts.filter((c) => c.contact_bunch_id === record.id).length}{" "}
          contacts
        </Tag>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => dayjs(date).format("DD-MM-YYYY"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Bunch">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "green" }} />}
              onClick={() => handleEditBunch(record)}
            />
          </Tooltip>
          <Tooltip title="Delete Bunch">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => showDeleteBunchModal(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Spin size="large" tip="Loading contacts..." />
      </div>
    );

  return (
    <div className="p-4">
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2} style={{ marginBottom: 8 }}>
            Contact Management
          </Title>
          <Text type="secondary" style={{ fontSize: 16 }}>
            Manage your contacts and organize them into bunches
          </Text>
        </div>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Button
              block
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddContact}
            >
              Add Contact
            </Button>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Button
              block
              icon={<UsergroupAddOutlined />}
              onClick={handleAddBunch}
            >
              Add Bunch
            </Button>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Button
              block
              icon={<ImportOutlined />}
              onClick={handleImportContacts}
            >
              Import Excel
            </Button>
          </Col>
        </Row>
      </Row>

      {error && (
        <div style={{ marginBottom: 16 }}>
          <Text type="danger">{error}</Text>
        </div>
      )}

      <Tabs activeKey={activeTabKey} onChange={setActiveTabKey}>
        <TabPane tab={`Bunches (${contactBunches.length})`} key="bunches">
          {contactBunches.length === 0 ? (
            <Empty
              description="No contact bunches found"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button type="primary" onClick={handleAddBunch}>
                Create Your First Bunch
              </Button>
            </Empty>
          ) : (
            <>
              <div className="rounded-2 table-responsive">
                <table
                  className="table align-middle mb-0 shadow-sm"
                  style={{ tableLayout: "auto" }}
                >
                  <thead className="table-light">
                    <tr>
                      <th>Bunch Name</th>
                      <th>Bunch Description</th>
                      <th>No of Contacts</th>
                      <th>Created Date</th>
                      <th style={{ width: "100px" }} className="text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {contactBunches
                      .slice(
                        (bunchPagination.current - 1) *
                          bunchPagination.pageSize,
                        bunchPagination.current * bunchPagination.pageSize
                      )
                      .map((bunch) => (
                        <tr key={bunch.id}>
                          <td style={{ fontWeight: 600 }}>{bunch.name}</td>
                          <td>{bunch.description || "No description"}</td>
                          <td>
                            <Tag color="blue">
                              {
                                contacts.filter(
                                  (c) => c.contact_bunch_id === bunch.id
                                ).length
                              }{" "}
                              contacts
                            </Tag>
                          </td>
                          <td>
                            {dayjs(bunch.created_at).format("DD-MM-YYYY")}
                          </td>
                          <td
                            className="text-center"
                            style={{
                              paddingRight: "8px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Button
                              type="text"
                              icon={<EditOutlined style={{ color: "green" }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditBunch(bunch);
                              }}
                            />
                            <Button
                              type="text"
                              icon={<DeleteOutlined style={{ color: "red" }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                showDeleteBunchModal(bunch);
                              }}
                            />

                            {/* <button
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => handleEditBunch(bunch)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => showDeleteBunchModal(bunch)}
                          >
                            Delete
                          </button> */}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-end">
                <CustomPagination
                  current={bunchPagination.current}
                  pageSize={bunchPagination.pageSize}
                  total={bunchPagination.total}
                  onPageChange={handleBunchPageChange}
                  onPageSizeChange={handleBunchPageSizeChange}
                />
              </div>
            </>
          )}
        </TabPane>

        <TabPane tab={`Contacts (${contacts.length})`} key="contacts">
          {contacts.length === 0 ? (
            <Empty
              description="No contacts found"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button type="primary" onClick={handleAddContact}>
                Create Your First Contact
              </Button>
            </Empty>
          ) : (
            <>
              <div className="rounded-2 table-responsive">
                <table
                  className="table align-middle mb-0 shadow-sm"
                  style={{ tableLayout: "auto" }}
                >
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Address</th>
                      <th>Language</th>
                      <th>Bunch</th>
                      <th>Notes</th>
                      <th>Created</th>
                      <th style={{ width: "100px" }} className="text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts
                      .slice(
                        (pagination.current - 1) * pagination.pageSize,
                        pagination.current * pagination.pageSize
                      )
                      .map((contact) => (
                        <tr key={contact.id}>
                          <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <div
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  backgroundColor: "#1890ff",
                                  color: "white",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 14,
                                  fontWeight: "bold",
                                }}
                              >
                                {contact.first_name?.charAt(0)?.toUpperCase() ||
                                  "?"}
                              </div>
                              <div style={{ fontWeight: 600 }}>
                                {contact.fullName}
                              </div>
                            </div>
                          </td>
                          <td>{contact.phone || "-"}</td>
                          <td>{contact.email || "-"}</td>
                          <td>{contact.address || "N/A"}</td>
                          <td>{contact.language || "N/A"}</td>
                          <td>{contact.contact_bunch_name || "N/A"}</td>
                          <td>{contact.notes || "N/A"}</td>
                          <td>
                            {dayjs(contact.created_at).format("DD-MM-YYYY")}
                          </td>
                          <td
                            style={{
                              paddingRight: "8px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Button
                              type="text"
                              icon={<EditOutlined style={{ color: "green" }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditContact(contact);
                              }}
                            />
                            <Button
                              type="text"
                              icon={<DeleteOutlined style={{ color: "red" }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                showDeleteContactModal(contact);
                              }}
                            />
                            {/* <button
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => handleEditContact(contact)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => showDeleteContactModal(contact)}
                            >
                              Delete
                            </button> */}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-end">
                <CustomPagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              </div>
            </>
          )}
        </TabPane>
      </Tabs>

      {/* <Modal
        title={editingContact ? "Edit Contact" : "Add New Contact"}
        open={showForm}
        onCancel={handleFormClose}
        footer={null}
        width={600}
        destroyOnClose
      >
        <ContactForm
          contact={editingContact}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      </Modal> */}

      <ContactFormModal
        showForm={showForm}
        setShowForm={setShowForm}
        contact={editingContact}
        onSubmit={handleFormSubmit}
      />

      {/* <Modal
        title={editingBunch ? "Edit Bunch" : "Add New Bunch"}
        open={showBunchForm}
        onCancel={handleFormClose}
        footer={null}
        width={600}
        destroyOnClose
      >
        <ContactBunchForm
          bunch={editingBunch}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      </Modal> */}

      <ContactBunchFormModal
        showForm={showBunchForm}
        setShowForm={setShowBunchForm}
        bunch={editingBunch}
        onSubmit={handleFormSubmit}
      />

      <Modal
        title="Import Contacts"
        open={showImportForm}
        onCancel={handleFormClose}
        footer={null}
        width={800}
        destroyOnClose
      >
        <ContactImport
          onClose={handleFormClose}
          onImportSuccess={handleFormSubmit}
        />
      </Modal>

      {/* <ContactImportModal 
        show={showImportForm}
        setShow={setShowImportForm}
        onImportSuccess={handleFormSubmit}
      /> */}

      <Modal
        title="Delete Contact"
        open={isDeleteContactModalVisible}
        onOk={handleDeleteContact}
        onCancel={() => setIsDeleteContactModalVisible(false)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{
          style: { backgroundColor: "red", borderColor: "red" },
        }}
      >
        <p>Are you sure you want to delete this contact?</p>
      </Modal>

      <Modal
        title="Delete Bunch"
        open={isDeleteBunchModalVisible}
        onOk={handleDeleteBunch}
        onCancel={() => setIsDeleteBunchModalVisible(false)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{
          style: { backgroundColor: "red", borderColor: "red" },
        }}
      >
        <p>Are you sure you want to delete this contact bunch?</p>
      </Modal>
    </div>
  );
};

export default ContactList;
